import React, { useState } from "react";
import { X, Loader2, Send } from "lucide-react";
import toast from "react-hot-toast";

import { sendProposal } from "../api/prospectingReportsApi";

const field =
  "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55c5d0]/40";
const labelCls = "block text-xs font-semibold text-gray-500 mb-1";

/** Send a proposal to its vendor. Guards against an accidental double-send (the
 *  API returns 409; we offer a confirm to resend). */
const SendProposalModal = ({ proposal, onClose, onSent }) => {
  const [email, setEmail] = useState(proposal.recipient_email || "");
  const [name, setName] = useState(proposal.recipient_name || "");
  const [message, setMessage] = useState("");
  const [ownerEmails, setOwnerEmails] = useState((proposal.vendor_emails || []).join(", "));
  const [sending, setSending] = useState(false);

  const doSend = async (force = false) => {
    if (!email.trim()) {
      toast.error("Add the vendor's email.");
      return;
    }
    setSending(true);
    try {
      // Co-owner allowlist (optional): only these emails may approve, one each.
      const vendorEmails = ownerEmails
        .split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean);
      // Always send the allowlist explicitly ([] clears it) so a changed or
      // removed owner list is applied on the backend, never left stale.
      const body = {
        recipient_email: email.trim(), recipient_name: name.trim(), message: message.trim(),
        force, vendor_emails: vendorEmails,
      };
      const { data } = await sendProposal(proposal.id, body);
      toast.success("Proposal sent.");
      onSent(data);
    } catch (e) {
      if (e?.response?.status === 409) {
        setSending(false);
        if (window.confirm("This proposal was already sent. Send it again?")) {
          doSend(true);
        }
        return;
      }
      toast.error(e?.response?.data?.detail || "Could not send the proposal.");
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Send proposal</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 gap-2">
            <div className="mb-3">
              <label className={labelCls}>Vendor name</label>
              <input className={field} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="mb-3">
              <label className={labelCls}>Vendor email</label>
              <input className={field} value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <div className="mb-3">
            <label className={labelCls}>Co-owner emails (optional, for multiple owners)</label>
            <textarea rows={2} className={field} value={ownerEmails}
              onChange={(e) => setOwnerEmails(e.target.value)}
              placeholder="owner1@example.com, owner2@example.com" />
            <p className="text-[11px] text-gray-400 mt-1">
              Add one email per owner if the proposal needs more than one owner to accept. Only these owners can accept, once each.
            </p>
          </div>
          <div className="mb-1">
            <label className={labelCls}>Personal message (optional)</label>
            <textarea rows={3} className={field} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <p className="text-xs text-gray-400">Sent from your name, with a link the vendor can open on any device.</p>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700">Cancel</button>
          <button onClick={() => doSend(false)} disabled={sending}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0f8d9b] text-white text-sm font-semibold disabled:opacity-50">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendProposalModal;
